import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  FileText,
  MessageSquare,
  Scale,
  ShieldCheck,
  Sparkles,
  Users,
  Workflow,
} from 'lucide-react';

const features = [
  {
    icon: <MessageSquare />,
    title: '구조화 AI 인터뷰',
    description: 'BEI 기반 질문과 소크라틱 후속 질문으로 후보자의 실제 행동 사건과 판단 근거를 깊이 있게 확인합니다.',
  },
  {
    icon: <Workflow />,
    title: '직무별 역량 모델링',
    description: '직무에 필요한 역량과 행동 지표를 정의하고, 조직에 맞는 평가 템플릿과 루브릭으로 관리합니다.',
  },
  {
    icon: <BarChart3 />,
    title: '근거 기반 루브릭 채점',
    description: '핵심 발화를 증거로 연결해 역량별 점수를 산출하고, 평가 결과가 나온 이유를 함께 보여줍니다.',
  },
  {
    icon: <FileText />,
    title: '즉시 생성되는 HR 리포트',
    description: '인터뷰 종료 후 강점, 우려 사항, 역량별 근거와 후속 면접 가이드를 하나의 리포트로 정리합니다.',
  },
  {
    icon: <Users />,
    title: '평가 운영 워크스페이스',
    description: '후보자 초대부터 진행 현황, 평가 기록, 검토와 확정까지 HR 평가의 전체 흐름을 한곳에서 운영합니다.',
  },
  {
    icon: <ShieldCheck />,
    title: '검토 가능한 의사결정 지원',
    description: 'AI가 결정을 대신하지 않습니다. 사람의 최종 판단을 돕도록 검토·확정·감사 기록과 권한 체계를 제공합니다.',
  },
];

const steps = [
  ['01', '평가 기준 설계', '직무와 조직에 맞는 핵심 역량, 행동 지표, 루브릭을 구성합니다.'],
  ['02', 'AI 구조화 인터뷰', '답변에 따라 후속 질문을 이어가며 평가에 필요한 행동 근거를 수집합니다.'],
  ['03', '근거 중심 분석', '발화 속 실제 근거를 역량별 루브릭과 연결해 일관된 평가 초안을 만듭니다.'],
  ['04', '사람의 검토와 확정', 'HR 담당자가 결과와 신뢰도를 검토하고 최종 의사결정 자료로 확정합니다.'],
];

const HuraPage: React.FC = () => (
  <PageContainer>
    <HeroSection>
      <HeroContent>
        <Eyebrow><Sparkles /> HUMAN RESOURCE ASSESSMENT</Eyebrow>
        <HeroTitle>
          사람을 더 깊이 이해하는<br />
          <HuraTitleText>HuRA</HuraTitleText>
        </HeroTitle>
        <HeroTagline>AI 기반 구조화 HR 역량 평가 솔루션</HeroTagline>
        <HeroDescription>
          HuRA는 SOCRATEST의 소크라틱 트리 엔진을 HR 평가에 확장한 FreakIT의 핵심 솔루션입니다.
          직무별 역량을 설계하고, 구조화 인터뷰에서 행동 근거를 수집한 뒤,
          루브릭 기반의 설명 가능한 평가 리포트로 HR의 판단을 지원합니다.
        </HeroDescription>
        <BadgeRow>
          {['BEI 구조화 인터뷰', '실시간 후속 질문', '근거 기반 채점', 'HR 리포트', '검토·감사 체계'].map((badge) => (
            <Badge key={badge}>{badge}</Badge>
          ))}
        </BadgeRow>
        <HeroActions>
          <PrimaryButton to="/contact">도입 문의하기 <ArrowRight /></PrimaryButton>
          <TextLink href="#how-it-works">평가 방식 살펴보기</TextLink>
        </HeroActions>
      </HeroContent>
    </HeroSection>

    <IntroSection>
      <ScrollFadeIn direction="up">
        <IntroGrid>
          <div>
            <SectionEyebrow>WHY HuRA</SectionEyebrow>
            <SectionTitle>인상 대신 근거로,<br />경험 대신 일관된 기준으로</SectionTitle>
          </div>
          <IntroDescription>
            <p>
              면접관마다 달라지는 질문과 판단 기준은 좋은 인재를 놓치게 만듭니다.
              HuRA는 모든 후보자에게 구조화된 평가 기회를 제공하고, 답변 속 구체적인 행동 근거를
              동일한 루브릭으로 해석합니다.
            </p>
            <PrincipleList>
              <Principle><CheckCircle2 /> 실제 행동 사건을 끝까지 확인</Principle>
              <Principle><CheckCircle2 /> 점수와 원문 근거를 함께 제시</Principle>
              <Principle><CheckCircle2 /> AI 초안 위에 사람의 최종 검토</Principle>
            </PrincipleList>
          </IntroDescription>
        </IntroGrid>
      </ScrollFadeIn>
    </IntroSection>

    <FeatureSection>
      <SectionHeader>
        <SectionEyebrow>CAPABILITIES</SectionEyebrow>
        <SectionTitle>평가의 처음부터 마지막까지</SectionTitle>
        <SectionDescription>
          역량 모델링, 인터뷰, 분석, 리포트와 거버넌스를 하나의 흐름으로 연결합니다.
        </SectionDescription>
      </SectionHeader>
      <FeatureGrid>
        {features.map((feature, index) => (
          <ScrollFadeIn key={feature.title} direction="up" delay={0.08 * (index + 1)}>
            <FeatureCard>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
        ))}
      </FeatureGrid>
    </FeatureSection>

    <ProcessSection id="how-it-works">
      <SectionHeader>
        <SectionEyebrow>HOW IT WORKS</SectionEyebrow>
        <SectionTitle>일관된 평가를 만드는 4단계</SectionTitle>
      </SectionHeader>
      <StepGrid>
        {steps.map(([number, title, description], index) => (
          <ScrollFadeIn key={number} direction="up" delay={0.1 * (index + 1)}>
            <StepCard>
              <StepNumber>{number}</StepNumber>
              <StepTitle>{title}</StepTitle>
              <StepDescription>{description}</StepDescription>
            </StepCard>
          </ScrollFadeIn>
        ))}
      </StepGrid>
    </ProcessSection>

    <TrustSection>
      <ScrollFadeIn direction="up">
        <TrustCard>
          <TrustIcon><Scale /></TrustIcon>
          <div>
            <SectionEyebrow>HUMAN IN THE LOOP</SectionEyebrow>
            <TrustTitle>HuRA는 사람을 대신해 결정하지 않습니다</TrustTitle>
            <TrustDescription>
              AI 결과는 최종 판정이 아닌 검토 가능한 평가 초안입니다. 근거가 부족하거나 신뢰도가 낮은
              결과는 명확히 표시하고, 권한을 가진 평가자가 검토·확정하도록 설계했습니다.
              채용과 인사 의사결정의 책임은 언제나 사람에게 있습니다.
            </TrustDescription>
          </div>
        </TrustCard>
      </ScrollFadeIn>
    </TrustSection>

    <CTASection>
      <CTAContent>
        <CTATitle>더 공정하고 설명 가능한 평가를 시작하세요</CTATitle>
        <CTADescription>조직의 직무와 평가 환경에 맞는 HuRA 도입 방법을 함께 설계합니다.</CTADescription>
        <CTAButton to="/contact">HuRA 도입 문의 <ArrowRight /></CTAButton>
      </CTAContent>
    </CTASection>
  </PageContainer>
);

export default HuraPage;

const PageContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  position: relative;
  min-height: 760px;
  padding: 7rem 2rem 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 980px;
  text-align: center;
`;

const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
  color: #65d6c6;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  svg { font-size: 1rem; }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: clamp(3rem, 7vw, 5.5rem);
  line-height: 1.08;
  letter-spacing: -0.04em;
  margin-bottom: 1.4rem;
`;

const HuraTitleText = styled.span`
  color: #62d7c5;
`;

const HeroTagline = styled.p`
  color: #d3f5ef;
  font-size: clamp(1.15rem, 2.5vw, 1.5rem);
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const HeroDescription = styled.p`
  max-width: 820px;
  margin: 0 auto 2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.9;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.7rem;
  margin-bottom: 2.5rem;
`;

const Badge = styled.span`
  padding: 0.55rem 1rem;
  border: 1px solid rgba(101, 214, 198, 0.25);
  background: rgba(101, 214, 198, 0.07);
  border-radius: 999px;
  color: #b8efe7;
  font-size: 0.88rem;
  font-weight: 600;
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.25rem;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.9rem 1.5rem;
  border-radius: 9px;
  background: white;
  color: #020617;
  font-weight: 800;
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover { transform: translateY(-3px); box-shadow: 0 14px 35px rgba(0, 0, 0, 0.3); }
`;

const TextLink = styled.a`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
  padding-bottom: 0.2rem;
`;

const IntroSection = styled.section`
  padding: 6rem 2rem;
`;

const IntroGrid = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 5rem;
  align-items: start;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const SectionEyebrow = styled.p`
  color: #65d6c6;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.17em;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: clamp(2rem, 4vw, 2.75rem);
  line-height: 1.25;
  letter-spacing: -0.03em;
`;

const SectionDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.8;
  margin-top: 1rem;
`;

const IntroDescription = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.08rem;
  line-height: 1.9;
`;

const PrincipleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 2rem;
`;

const Principle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 600;
  svg { color: #65d6c6; }
`;

const FeatureSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-block: 1px solid rgba(255, 255, 255, 0.06);
`;

const SectionHeader = styled.div`
  max-width: 760px;
  margin: 0 auto 3.5rem;
  text-align: center;
`;

const FeatureGrid = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.4rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.article`
  height: 100%;
  padding: 1.8rem;
  background: rgba(15, 23, 42, 0.38);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover { transform: translateY(-5px); border-color: rgba(101, 214, 198, 0.32); }
`;

const FeatureIcon = styled.div`
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 11px;
  background: rgba(101, 214, 198, 0.09);
  color: #65d6c6;
  font-size: 1.35rem;
  margin-bottom: 1.25rem;
`;

const FeatureTitle = styled.h3`
  color: white;
  font-size: 1.18rem;
  margin-bottom: 0.75rem;
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.75;
`;

const ProcessSection = styled.section`
  padding: 6rem 2rem;
  scroll-margin-top: 90px;
`;

const StepGrid = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { grid-template-columns: 1fr; }
`;

const StepCard = styled.article`
  height: 100%;
  padding: 1.6rem;
  border-top: 2px solid rgba(101, 214, 198, 0.5);
  background: rgba(15, 23, 42, 0.2);
`;

const StepNumber = styled.span`
  display: block;
  color: #65d6c6;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 1.2rem;
`;

const StepTitle = styled.h3`
  color: white;
  font-size: 1.15rem;
  margin-bottom: 0.75rem;
`;

const StepDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
`;

const TrustSection = styled.section`
  padding: 1rem 2rem 6rem;
`;

const TrustCard = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2.5rem;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  border: 1px solid rgba(94, 234, 212, 0.22);
  background: rgba(20, 184, 166, 0.055);
  border-radius: 18px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    padding: 2rem;
  }
`;

const TrustIcon = styled.div`
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: rgba(94, 234, 212, 0.1);
  color: #62d7c5;
  font-size: 1.6rem;
`;

const TrustTitle = styled.h2`
  color: white;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  margin-bottom: 1rem;
`;

const TrustDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.85;
`;

const CTASection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid rgba(255, 255, 255, 0.07);
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const CTATitle = styled.h2`
  color: white;
  font-size: clamp(2rem, 4vw, 2.75rem);
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.95rem 1.6rem;
  border-radius: 9px;
  background: white;
  color: #020617;
  font-weight: 800;
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover { transform: translateY(-3px); box-shadow: 0 14px 35px rgba(0, 0, 0, 0.3); }
`;
